// Slot de item: mostra o sprite, a quantidade e a moldura de raridade; aceita arrastar e soltar.
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ItemInstance, MoveTarget } from '../../../../shared/src/game/items';
import { RARITY_NAMES } from '../../../../shared/src/game/items';
import { catalogNow, itemImage } from '../items';

/** Nome ao passar o mouse: item, upgrade e raridade. */
function itemTitle(i: ItemInstance) {
  const n = catalogNow()?.get(i.typeId)?.name ?? '';
  return `${i.count > 1 ? i.count + 'x ' : ''}${n}${i.upgrade ? ' +' + i.upgrade : ''}${i.rarity ? ` (${RARITY_NAMES[i.rarity]})` : ''}`;
}

export interface DragApi {
  onDrop: (itemId: number, to: MoveTarget, ctrl: boolean) => void;
  onContext: (item: ItemInstance, x: number, y: number) => void;
  onOpen: (item: ItemInstance) => void;
}

export function ItemImg(props: { typeId: number; count: number }) {
  const [src, setSrc] = useState('');
  useEffect(() => { let ok = true; itemImage(props.typeId, props.count).then((s) => ok && setSrc(s)); return () => { ok = false; }; }, [props.typeId, props.count]);
  return src ? <img class="item-img" src={src} width={32} height={32} alt="" draggable={false} /> : null;
}

/** tipo do último toque (no celular, tocar num item abre o menu dele) */
let lastPointer = 'mouse';
let tapTimer = 0, lastTapId = 0, lastTapAt = 0;
let downAt: { x: number; y: number } | null = null;
/** janela do toque duplo (um pouco maior que no PC: no celular o dedo é mais lento) */
const DOUBLE_TAP_MS = 450;
/** segundo toque aceito até este tempo depois do primeiro; se o menu já abriu, ele fecha e o item é usado */
const DOUBLE_TAP_LATE_MS = 750;

// --- arrastar com o dedo (celular): segurar ~0,35 s no item e arrastar até outro espaço (mochila, depot, equipamento, barra) ---
// O arrastar nativo do navegador (draggable) não funciona com toque, então o jogo faz o próprio: um "fantasma" do item segue
// o dedo e, ao soltar, o espaço embaixo do dedo recebe o evento "nxdrop" (o ItemSlot e a barra de ações escutam).
const LONG_PRESS_MS = 350;
let press: { id: number; x: number; y: number; timer: number; img: string; onLong: () => void } | null = null;
let drag: { id: number; ghost: HTMLElement; moved: boolean; over: Element | null } | null = null;
/** o último toque virou arraste (ou toque longo): o pointerup do slot não deve tratar como toque */
let dragged = false;
function dropTargetAt(x: number, y: number) { return document.elementFromPoint(x, y)?.closest('[data-nxdrop]') ?? null; }
function endDrag(x: number, y: number, cancel = false) {
  const d = drag; drag = null;
  if (!d) return;
  d.ghost.remove();
  d.over?.classList.remove('over');
  if (cancel) return;
  if (!d.moved) { press?.onLong(); return; }   // segurou sem arrastar: abre o menu do item (como o toque longo)
  const t = dropTargetAt(x, y);
  if (t) t.dispatchEvent(new CustomEvent('nxdrop', { detail: { id: d.id }, bubbles: true }));
}
let touchInstalled = false;
function installTouchDrag() {
  if (touchInstalled || typeof window === 'undefined') return;
  touchInstalled = true;
  window.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'touch') return;
    if (press && !drag && Math.hypot(e.clientX - press.x, e.clientY - press.y) > 12) { clearTimeout(press.timer); press = null; return; }   // rolando a tela
    if (!drag) return;
    if (Math.hypot(e.clientX - (press?.x ?? e.clientX), e.clientY - (press?.y ?? e.clientY)) > 8) drag.moved = true;
    drag.ghost.style.transform = `translate(${e.clientX - 24}px, ${e.clientY - 56}px)`;
    const t = dropTargetAt(e.clientX, e.clientY);
    if (t !== drag.over) { drag.over?.classList.remove('over'); t?.classList.add('over'); drag.over = t; }
  });
  // enquanto arrasta, o dedo não rola a página
  window.addEventListener('touchmove', (e) => { if (drag) e.preventDefault(); }, { passive: false });
  window.addEventListener('pointerup', (e) => {
    if (e.pointerType !== 'touch') return;
    if (press && !drag) { clearTimeout(press.timer); press = null; }
    if (drag) { endDrag(e.clientX, e.clientY); press = null; }
  });
  window.addEventListener('pointercancel', (e) => {
    if (e.pointerType !== 'touch') return;
    if (press) { clearTimeout(press.timer); press = null; }
    if (drag) endDrag(e.clientX, e.clientY, true);
  });
}
/** começa a contar o toque longo num item (chamado no pointerdown de toque) */
export function startTouchPress(id: number, x: number, y: number, img: string, onLong: () => void) {
  installTouchDrag();
  if (press) clearTimeout(press.timer);
  dragged = false;
  press = { id, x, y, img, onLong, timer: window.setTimeout(() => {
    if (!press) return;
    dragged = true;
    if (tapTimer) { clearTimeout(tapTimer); tapTimer = 0; }
    const ghost = document.createElement('div');
    ghost.className = 'touch-ghost';
    ghost.innerHTML = press.img ? `<img src="${press.img}" width="48" height="48" alt="">` : '';
    ghost.style.transform = `translate(${press.x - 24}px, ${press.y - 56}px)`;
    document.body.appendChild(ghost);
    drag = { id: press.id, ghost, moved: false, over: null };
    try { navigator.vibrate?.(15); } catch { /* sem vibração */ }
  }, LONG_PRESS_MS) };
}
/** recebe itens soltos pelo arraste com o dedo (o elemento precisa ter o atributo data-nxdrop) */
export function useTouchDrop(onDrop: (id: number) => void) {
  const ref = useRef<HTMLElement | null>(null);
  const cb = useRef(onDrop); cb.current = onDrop;
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const h = (e: Event) => { if (e.target !== el) return; cb.current((e as CustomEvent<{ id: number }>).detail.id); };
    el.addEventListener('nxdrop', h);
    return () => el.removeEventListener('nxdrop', h);
  }, []);
  return ref;
}

export function ItemSlot(props: {
  item?: ItemInstance; target: MoveTarget; api: DragApi; placeholder?: string; title?: string; children?: ComponentChildren;
}) {
  const [over, setOver] = useState(false);
  const { item } = props;
  const dropRef = useTouchDrop((id) => { if (id && id !== item?.id) props.api.onDrop(id, props.target, false); });
  return (
    <span
      ref={dropRef as any}
      data-nxdrop="1"
      class={'item-slot' + (over ? ' over' : '') + (item?.rarity ? ` rarity-${item.rarity}` : '')}
      title={props.title ?? (item ? itemTitle(item) : undefined)}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setOver(false);
        const id = Number(e.dataTransfer?.getData('text/plain'));
        if (id && id !== item?.id) props.api.onDrop(id, props.target, e.ctrlKey);
      }}
      onContextMenu={(e) => { if (!item) return; e.preventDefault(); if (lastPointer === 'touch') return; props.api.onContext(item, e.clientX, e.clientY); }}   // no celular o toque longo é do arraste
      onPointerDown={(e) => {
        lastPointer = e.pointerType;
        if (e.pointerType !== 'touch') return;
        downAt = { x: e.clientX, y: e.clientY };
        if (item) {
          const img = (e.currentTarget as HTMLElement).querySelector('img.item-img') as HTMLImageElement | null;
          const x = e.clientX, y = e.clientY, it = item;
          startTouchPress(item.id, x, y, img?.src ?? '', () => props.api.onContext(it, x, y));
        }
      }}
      onPointerUp={(e) => {
        // celular: um toque abre o menu do item; dois toques rápidos usam/equipam (como os dois cliques no PC).
        // Feito no pointerup (e não no click): no celular o navegador às vezes engole o segundo click do toque duplo (zoom).
        if (!item || e.pointerType !== 'touch') return;
        if (dragged) { dragged = false; return; }   // foi toque longo / arraste, não toque
        if (downAt && Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y) > 12) return;   // arrastou: não é toque
        const now = e.timeStamp || performance.now(), x = e.clientX, y = e.clientY;   // hora do toque (não a de quando o navegador processou)
        if (lastTapId === item.id && now - lastTapAt < DOUBLE_TAP_LATE_MS) {
          // mesmo que o menu já tenha aberto (celular lento), o segundo toque fecha o menu e usa o item
          if (tapTimer) clearTimeout(tapTimer); tapTimer = 0; lastTapAt = 0; e.preventDefault(); props.api.onOpen(item); return; }
        if (tapTimer) clearTimeout(tapTimer);
        lastTapId = item.id; lastTapAt = now;
        tapTimer = window.setTimeout(() => { tapTimer = 0; props.api.onContext(item, x, y); }, DOUBLE_TAP_MS);
      }}
      onDblClick={() => item && lastPointer !== 'touch' && props.api.onOpen(item)}
    >
      {!item && props.placeholder && <img class="item-ph" src={props.placeholder} width={32} height={32} alt="" draggable={false} />}
      {item && (
        <span class="item-drag" draggable onDragStart={(e) => { if (lastPointer === 'touch') { e.preventDefault(); return; } e.dataTransfer!.setData('text/plain', String(item.id)); e.dataTransfer!.effectAllowed = 'move'; }}>
          <ItemImg typeId={item.typeId} count={item.count} />
          {item.count > 1 && <span class="item-count">{item.count}</span>}
          {item.upgrade ? <span class="item-upgrade">+{item.upgrade}</span> : null}
        </span>
      )}
      {props.children}
    </span>
  );
}
